/**
 * The two messages staff hand on from an incident: what to tell the customer (plain, no codes, Paige and Cortexa
 * only) and what to give engineering (ids, the error, the tries, the signals, a timeline link and a log query).
 * Pure: the panel copies the text.
 */
import type { Formatter } from '@paige/format';
import type { FlowIncident } from './flow-types';
import {
  customerSees,
  handlingWords,
  incidentHeadline,
  incidentSize,
  signalWords,
} from './incident-words';

const TELL: Record<string, string> = {
  source_file_unreadable:
    'Paige could not open {file}. It may be damaged, password-protected or saved in another format. Please send a fresh copy (a PDF or an image straight from the scanner works best); nothing was charged for it.',
  source_file_set_aside:
    'Paige could not open {file} safely, so it set the file aside to keep everything else moving. The rest of the batch was read. Please send a fresh copy; our team is looking at why.',
  unsupported_format:
    'Paige does not read {file} in its current format. Please send it as a PDF or an image.',
  destination_auth_failed:
    'Your destination stopped accepting Paige’s saved sign-in. Please reconnect it in the job’s settings; the waiting documents go out as soon as you do.',
  destination_mapping_invalid:
    'Your destination refused a field Paige sent. Please check the job’s field mapping; the waiting documents go out once it is fixed.',
  credits_exhausted:
    'Your company has used its pages, so new work waits. Add pages and it continues by itself.',
};

const OURS =
  'Some of your documents ({what}) hit a problem on our side while Paige was working on them. Our team is on it, and nothing needs doing on yours; we will let you know when they are through.';

/** The message for the company, ready to paste into an email or a chat. */
export function tellCustomer(
  f: Formatter,
  i: FlowIncident,
  company: string,
  file?: string,
): string {
  const named = file ? `“${file}”` : 'one of your files';
  const body = (TELL[i.reason ?? ''] ?? OURS)
    .replace('{file}', named)
    .replace('{what}', `${f.int(i.items)} ${i.unit}`);
  return `Hello ${company} team,\n\n${body}\n\nThe Paige team at Ademero`;
}

/** A Cloud Logging query over the hour the incident started, for the companies it names. */
export function logsLink(project: string, i: FlowIncident, tenants: string[]): string {
  const from = new Date(Date.parse(i.startedAt) - 10 * 60_000).toISOString();
  const ids = tenants.map((t) => `jsonPayload.tenant_id="${t}"`).join(' OR ');
  const query = `severity>=WARNING${ids ? `\n(${ids})` : ''}`;
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(query)};startTime=${from}?project=${encodeURIComponent(project)}`;
}

export interface EngineeringContext {
  origin: string;
  /** Links to the panel's companies on Problems, where each file opens to its story; at most a few. */
  timelines: string[];
  logsProject: string | null;
  tenants: string[];
  asOf: string;
}

/** The report for engineering: everything the panel knows, in one paste. */
export function engineeringReport(f: Formatter, i: FlowIncident, x: EngineeringContext): string {
  const lines = [
    `Stages and queues incident ${i.id} (${incidentHeadline(i)})`,
    `Where: ${i.places.join(', ')}; class ${i.class}, reason ${i.reason ?? 'none'}, handling ${i.handling}`,
    `Size: ${incidentSize(f, i)}; started ${i.startedAt}; as of ${x.asOf}`,
    `Oldest: ${i.top.name} (${i.top.id})${i.top.jobName ? `, job ${i.top.jobName} (${i.top.jobId ?? ''})` : ''}`,
    `Tries: ${i.attempts ?? '-'} of ${i.maxAttempts ?? '-'}; auto retry ${i.autoRetry ?? '-'}; behind it ${i.behind}`,
    `Paige: ${handlingWords(f, i)}`,
    `Customer sees: ${customerSees(i) ?? 'nothing'}`,
    `Last error: ${i.detail ?? '(not shown to this staff role)'}`,
    ...i.signals.map((s) => `Signal: ${signalWords(s)}`),
    ...x.timelines.map((t) => `Open: ${x.origin}${t}`),
  ];
  if (x.logsProject) lines.push(`Logs: ${logsLink(x.logsProject, i, x.tenants)}`);
  return lines.join('\n');
}
